import { alpha } from '@mui/material/styles';

// ----------------------------------------------------------------------

const GREY = {
  0: '#FFFFFF',
  100: '#F9FAFB',
  200: '#F4F6F8',
  300: '#DFE3E8',
  400: '#C4CDD5',
  500: '#919EAB',
  600: '#637381',
  700: '#454F5B',
  800: '#212B36',
  900: '#161C24',
};

const SECONDARY = {
  lighter: '#D6E4FF',
  light: '#84A9FF',
  main: '#3366FF',
  dark: '#1939B7',
  darker: '#091A7A',
  contrastText: '#fff',
};

const HEADERINFO = {
  lighter: '#FFFFFF',
  light: '#FFA48D',
  main: '#FF4842',
  dark: '#B72136',
  darker: '#7A0C2E',
  contrastText: '#fff',
};

// Exports a dynamic, high-contrast palette based on active selection (GitHub Dark Proportions)
export const getPalette = (mode) => {
  const isLight = mode === 'light';

  // Dynamic primary and status colors optimized for both light pastels and dark SaaS console layouts
  const PRIMARY = {
    lighter: isLight ? '#D1E9FC' : 'rgba(32, 101, 209, 0.08)', // Card 4 Fix: Faint blue overlay on dark mode
    light: '#76B0F1',
    main: '#2065D1',
    dark: '#103996',
    darker: isLight ? '#061B64' : '#76B0F1', // High-contrast light blue text on dark mode
    contrastText: '#fff',
  };

  const INFO = {
    lighter: isLight ? '#D0F2FF' : 'rgba(24, 144, 255, 0.08)', // Faint blue overlay
    light: '#74CAFF',
    main: '#1890FF',
    dark: '#0C53B7',
    darker: isLight ? '#04297A' : '#74CAFF', // High-contrast light blue text on dark mode
    contrastText: '#fff',
  };

  const SUCCESS = {
    lighter: isLight ? '#E9FCD4' : 'rgba(84, 214, 44, 0.08)', // Faint green overlay
    light: '#AAF27F',
    main: '#54D62C',
    dark: '#229A16',
    darker: isLight ? '#08660D' : '#AAF27F', // High-contrast light green text on dark mode
    contrastText: isLight ? GREY[800] : '#fff',
  };

  const WARNING = {
    lighter: isLight ? '#FFF7CD' : 'rgba(255, 193, 7, 0.08)', // Faint gold/yellow overlay
    light: '#FFE16A',
    main: '#FFC107',
    dark: '#B78103',
    darker: isLight ? '#7A4F01' : '#FFE16A', // High-contrast gold text on dark mode
    contrastText: isLight ? GREY[800] : '#fff',
  };

  const ERROR = {
    lighter: isLight ? '#FFE7D9' : 'rgba(255, 72, 66, 0.08)', // Faint red overlay
    light: '#FFA48D',
    main: '#FF4842',
    dark: '#B72136',
    darker: isLight ? '#7A0C2E' : '#FFA48D', // High-contrast light red text on dark mode
    contrastText: '#fff',
  };

  return {
    mode: isLight ? 'light' : 'dark',
    common: { black: '#000', white: '#fff' },
    primary: PRIMARY,
    secondary: SECONDARY,
    info: INFO,
    success: SUCCESS,
    warning: WARNING,
    headerinfo: HEADERINFO,
    error: ERROR,
    grey: GREY,
    divider: isLight ? alpha(GREY[500], 0.24) : '#30363d', // GitHub's border grey
    text: {
      primary: isLight ? GREY[800] : '#c9d1d9', // GitHub's primary text
      secondary: isLight ? GREY[600] : '#8b949e', // GitHub's secondary text
      disabled: isLight ? GREY[500] : '#484f58',
    },
    background: {
      paper: isLight ? '#fff' : '#161b22', // GitHub's Card/Surface slate
      default: isLight ? GREY[100] : '#010409', // CORRECTED: Main page is ultra-deep black
      neutral: isLight ? GREY[200] : '#0d1117', // CORRECTED: Sidebar is lighter slate-navy
    },
    action: {
      active: isLight ? GREY[600] : '#8b949e',
      hover: isLight ? alpha(GREY[500], 0.08) : 'rgba(255,255,255,0.05)',
      selected: isLight ? alpha(GREY[500], 0.16) : 'rgba(255,255,255,0.08)',
      disabled: isLight ? alpha(GREY[500], 0.8) : 'rgba(255,255,255,0.3)',
      disabledBackground: isLight ? alpha(GREY[500], 0.24) : 'rgba(255,255,255,0.08)',
      focus: isLight ? alpha(GREY[500], 0.24) : 'rgba(255,255,255,0.08)',
      hoverOpacity: 0.08,
      disabledOpacity: 0.48,
    },
  };
};

const defaultPalette = getPalette('light');
export default defaultPalette;
